/**
 * Event Reporting API client (ASHI Marketing Event Reporting).
 *
 * Named eventReporting.ts because lib/events.ts already holds the chat SSE
 * event types.
 *
 * Endpoints (all under the portal's /api, cookie auth):
 *   GET  /api/events/appointments?start=&end=
 *                             Report 1 rows + refresh time + rates (event_viewer)
 *   GET  /api/events/config   rate history, newest first      (event_viewer)
 *   POST /api/events/config   add an effective-dated rate row   (event_editor)
 *   DELETE /api/events/config/{id}  soft-delete a rate row      (event_editor)
 *
 * Auth pattern mirrors assumptions/api.ts: fetch with credentials:"include".
 * The API enforces the group, and Supabase RLS enforces it again.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/** One effective-dated rate row. Percentages are fractions (0.05 = 5%). */
export interface EventConfigRow {
  id: number;
  commission_pct: number | null;
  override_pct: number | null;
  demo_pay_rate: number;
  effective_from: string; // YYYY-MM-DD
  updated_by: string | null;
  updated_at: string | null;
  /** Soft-deleted rows stay in the history; the report ignores them. */
  deleted_at: string | null;
  deleted_by: string | null;
  /** Event manager: LP promoter name exactly as on the leads ("Last, First"). */
  manager_promoter_name: string | null;
  /** Extra % the manager earns on her own sales (on top of the override on all sales). */
  manager_own_pct: number | null;
}

export interface EventConfigCreate {
  commission_pct: number | null;
  override_pct: number | null;
  demo_pay_rate: number;
  effective_from: string;
  manager_promoter_name: string | null;
  manager_own_pct: number | null;
}

async function errorFrom(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(text);
      return typeof parsed.detail === "string" ? parsed.detail : text;
    } catch {
      return text;
    }
  } catch {
    return `HTTP ${response.status}`;
  }
}

/** One Report 1 row (one event-sourced lead). Money is dollars; flags are funnel stages. */
export interface EventAppointment {
  lead_id: string;
  customer_name: string | null;
  subsource: string;
  entrydate: string | null; // YYYY-MM-DD
  apptdate: string | null; // ISO timestamp (UTC)
  product: string | null;
  disposition: string | null;
  display_label: string | null;
  gsa: number | null;
  net_sale_amount: number | null;
  commission_basis: number | null;
  commission: number | null; // null while the commission rate is unset
  override: number | null;
  demo_pay: number | null;
  promoter_name: string | null;
  /** Event manager in effect on this lead's date; she earns `override` on every sale. */
  manager_name: string | null;
  /** Manager's extra pay when this is her own sale; null otherwise. */
  manager_own: number | null;
  set_flag: boolean;
  issue_flag: boolean;
  na_flag: boolean;
  demo_flag: boolean;
  sale_flag: boolean;
  net_sale_flag: boolean;
}

export interface CurrentRates {
  commission_pct: number | null;
  override_pct: number | null;
  demo_pay_rate: number | null;
  effective_from: string | null;
  manager_promoter_name: string | null;
  manager_own_pct: number | null;
}

/** LP Event Hours per event x person x day; promoter_name = matching LP promoter, or null. */
export interface EventHours {
  subsource: string;
  person: string;
  promoter_name: string | null;
  work_date: string; // YYYY-MM-DD
  hours: number;
}

export interface EventAppointmentsResponse {
  refreshed_at: string | null;
  start: string;
  end: string;
  rates: CurrentRates | null;
  rows: EventAppointment[];
  hours: EventHours[];
}

export async function fetchEventAppointments(start: string, end: string): Promise<EventAppointmentsResponse> {
  const qs = new URLSearchParams({ start, end });
  const res = await fetch(`${API_BASE}/api/events/appointments?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

/** LP promoter names on event leads, for the event-manager picker. */
export async function fetchEventPromoters(): Promise<string[]> {
  const res = await fetch(`${API_BASE}/api/events/promoters`, { credentials: "include" });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

export async function fetchEventConfig(): Promise<EventConfigRow[]> {
  const res = await fetch(`${API_BASE}/api/events/config`, { credentials: "include" });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

export async function createEventConfig(body: EventConfigCreate): Promise<EventConfigRow> {
  const res = await fetch(`${API_BASE}/api/events/config`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

/** Soft-delete a rate row. 409 when it is the last live rate. */
export async function deleteEventConfig(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/events/config/${id}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) throw new Error(await errorFrom(res));
}

// ---------------------------------------------------------------- Event Recap (Report 2)

/** One row of vw_event_rollup: live LP-derived metrics for an event instance. */
export interface RecapRow {
  instance_id: string;
  subsource: string;
  display_name: string;
  start_date: string;
  end_date: string;
  attribution_end: string;
  year: number;
  leads: number | null;
  sets: number | null;
  issues: number | null;
  nas: number | null;
  demos: number | null;
  sales_count: number | null;
  sales_amount: number | null;
  avg_sale: number | null;
  closing_pct: number | null;
  commission_basis: number | null;
  employee_hours: number | null;
  cost_booth: number | null;
  cost_supplies: number | null;
  cost_misc_venue: number | null;
  cost_booth_total: number | null;
  cost_hourly_pay: number | null;
  commission: number | null;
  override: number | null;
  demo_pay: number | null;
  employee_cost: number | null;
  marketing_spend_total: number | null;
  cost_of_marketing_pct: number | null;
  rates_incomplete: boolean | null;
  commission_pct: number | null;
  override_pct: number | null;
  demo_pay_rate: number | null;
  unmatched_hours: number | null;
  unmatched_names: string[] | null;
  computed_at: string | null; // null = pending first refresh
  pipeline_run_id: string | null;
  manager_name: string | null;
  manager_own_pct: number | null;
  manager_own: number | null; // retired 2026-10-21 (always null)
  manager_pay: number | null; // manager override (override % x commission basis)
}

export interface RecapListItem {
  instance_id: string;
  subsource: string;
  display_name: string;
  start_date: string;
  end_date: string;
  attribution_end: string;
  year: number;
  leads: number | null;
  demos: number | null;
  sales_count: number | null;
  sales_amount: number | null;
  marketing_spend_total: number | null;
  cost_of_marketing_pct: number | null;
  computed_at: string | null;
  unmatched_hours: number | null;
  has_assumptions: boolean;
  last_saved_at: string | null;
}

export interface RecapPromoter {
  promoter_name: string;
  matched_to_leads: boolean;
  hours: number;
  leads: number;
  sets: number;
  demos: number;
  sales_count: number;
  sales_amount: number;
  commission_basis: number;
}

/** The eight assumption fields from Renee's email, plus notes. */
export interface RecapAssumptionsIn {
  venue: string | null;
  location: string | null;
  show_attendance: number | null;
  anticipated_net_revenue: number | null;
  total_vendors: number | null;
  weather: string | null;
  booth_location: string | null;
  booth_size: string | null;
  notes: string | null;
}

export interface RecapAssumptions extends RecapAssumptionsIn {
  updated_by: string;
  updated_by_email: string | null;
  updated_at: string;
}

export interface RecapSnapshot {
  id: number;
  rollup: RecapRow | null;
  rollup_promoter: RecapPromoter[] | null;
  assumptions: RecapAssumptionsIn | null;
  config: { commission_pct: number | null; override_pct: number | null; demo_pay_rate: number | null } | null;
  saved_by: string;
  saved_by_email: string | null;
  saved_at: string;
}

export interface RecapDetail {
  recap: RecapRow;
  promoters: RecapPromoter[];
  assumptions: RecapAssumptions | null;
  latest_snapshot: RecapSnapshot | null;
  snapshot_count: number;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface SubsourceOption {
  subsource: string;
  first_lead: string | null;
  last_lead: string | null;
  leads: number;
  instances: number;
}

export interface RecapInstanceIn {
  subsource: string;
  display_name: string;
  start_date: string;
  end_date: string;
  attribution_end: string | null;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

export const fetchRecaps = () => send<RecapListItem[]>("GET", "/api/events/instances");
export const fetchRecap = (id: string) => send<RecapDetail>("GET", `/api/events/instances/${encodeURIComponent(id)}`);
export const fetchSubsources = () => send<SubsourceOption[]>("GET", "/api/events/subsources");
export const createRecap = (body: RecapInstanceIn) =>
  send<{ instance_id: string }>("POST", "/api/events/instances", body);
export const patchRecap = (id: string, body: Partial<RecapInstanceIn>) =>
  send<RecapDetail>("PATCH", `/api/events/instances/${encodeURIComponent(id)}`, body);
export const saveRecapAssumptions = (id: string, body: RecapAssumptionsIn) =>
  send<RecapDetail>("PUT", `/api/events/instances/${encodeURIComponent(id)}/assumptions`, body);
export const fetchRecapCompare = (a: string, b: string) =>
  send<{ a: RecapDetail; b: RecapDetail }>(
    "GET", `/api/events/compare?${new URLSearchParams({ a, b })}`,
  );
